/**
 * Detect bots/crawlers/automated browsers by user agent string.
 *
 * Used to skip expensive client-side tools (e.g. LogRocket) for
 * non-human traffic.
 */

// Patterns that appear in known bot/crawler/tool user agents.
// Order: search engines, SEO tools, social/preview bots, headless
// browsers, generic indicators.
const BOT_UA_PATTERNS = [
  // Search engine crawlers
  'Googlebot',
  'Bingbot',
  'Slurp',           // Yahoo
  'DuckDuckBot',
  'Baiduspider',
  'YandexBot',
  'Sogou web spider',  // narrow to avoid matching SogouMobileBrowser (real browser)
  'Exabot',
  'ia_archiver',     // Alexa
  'AdsBot-Google',
  'Mediapartners-Google',
  'APIs-Google',
  'Google-InspectionTool',
  'GoogleOther',

  // SEO / marketing tools
  'AhrefsBot',
  'SemrushBot',
  'MJ12bot',         // Majestic
  'DotBot',          // Moz
  'Screaming Frog',
  'rogerbot',
  'Uptimebot',

  // Social media / preview bots
  'facebookexternalhit',
  'Facebot',
  'Twitterbot',
  'LinkedInBot',
  'Slackbot',
  'Discordbot',
  'TelegramBot',
  'Pinterestbot',

  // Headless / automated browsers
  'HeadlessChrome',
  'PhantomJS',
  'Selenium',
  'Puppeteer',
  'Playwright',

  // Monitoring / uptime
  'UptimeRobot',
  'pingdom',
  'Site24x7',
  'StatusCake',
  'Checkly',

  // Misc crawlers & tools
  'Chrome-Lighthouse',
  'BingPreview',
  'PetalBot',
  'MegaIndex',
  'Applebot',
  'DataForSeoBot',
  'GPTBot',
  'ChatGPT-User',
  'ClaudeBot',
  'anthropic-ai',
  'CCBot',
  'Bytespider',
  'Go-http-client',
  'python-requests',
  'axios',
  'node-fetch',
  'curl',
  'wget',
  'Java/',
  'libwww-perl',
  'Scrapy',
  'Camoufox',
  'PromptingBot',
  'PlayStore-Google',
  'Dart/',            // Dart HTTP client (mobile app, not a browser)
];

// Build a single case-insensitive regex for fast matching
const BOT_REGEX = new RegExp(
  BOT_UA_PATTERNS.map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|'),
  'i'
);

// Chromium-derivative browsers that include `Chrome/` in their UA but do not
// expose `window.chrome`. Keep this list next to isChromeLikeUA so updates
// stay in one place.
// Edge tokens: Edg/ (desktop), EdgA/ (Android), EdgiOS/ (iOS).
const CHROMIUM_DERIVATIVE_REGEX = /Edg(A|iOS)?\/|SamsungBrowser|OPR\/|YaBrowser|UCBrowser|HuaweiBrowser|MiuiBrowser|Brave\/|SogouMobileBrowser|QQBrowser|CriOS/;

/**
 * @param {string} [userAgent] - defaults to navigator.userAgent
 * @returns {boolean} true if the user agent looks like a bot
 */
const isBot = (userAgent) => {
  const ua = userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  if (!ua) return false;
  return BOT_REGEX.test(ua);
};

/**
 * True if the user agent claims to be Chrome-based but is NOT a known
 * Chromium-derivative browser (Samsung Internet, Opera, Yandex, UC, Huawei,
 * Xiaomi, Brave, Edge). Callers can combine this with a runtime check like
 * `typeof window.chrome === 'undefined'` to catch headless/spoofed Chrome.
 *
 * @param {string} [userAgent] - defaults to navigator.userAgent
 * @returns {boolean}
 */
const isChromeLikeUA = (userAgent) => {
  const ua = userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  if (!ua) return false;
  return /Chrome\//.test(ua) && !CHROMIUM_DERIVATIVE_REGEX.test(ua);
};

/**
 * Combined headless signal: both `navigator.plugins` is present-and-empty
 * AND `navigator.languages` is absent or empty. Neither signal is reliable
 * alone — iOS Safari has empty `navigator.plugins` by spec, and some
 * privacy-hardened browsers strip `navigator.languages` — but both together
 * is a strong indicator of a headless/automated browser.
 *
 * Returns false in non-browser environments (no navigator).
 *
 * @returns {boolean}
 */
const hasNoPluginsAndNoLanguages = () => {
  if (typeof navigator === 'undefined') return false;
  const hasNoPlugins = !!(navigator.plugins && navigator.plugins.length === 0);
  const hasNoLanguages = !navigator.languages || navigator.languages.length === 0;
  return hasNoPlugins && hasNoLanguages;
};

/**
 * Composite "this looks like a bot or a headless/automated browser" check,
 * combining every JS-level signal we trust: the `cca-app-tester` sentinel UA,
 * a bot user agent, `navigator.webdriver`, a Chrome-like UA with no
 * `window.chrome` object, and the empty-plugins+empty-languages headless tell.
 *
 * This is the same signal chain that `OpenReplayLazyLoader.shouldCreateOpenReplay`
 * and `LogRocketLazyLoader.shouldCreateLogRocket` apply per-tracker; centralizing
 * it here lets every session/analytics tracker (incl. Microsoft Clarity) honor
 * one consistent rule instead of drifting copies. Config/stage gating (project
 * keys, IS_DEVELOPMENT) stays with each caller.
 *
 * Returns false in non-browser environments (no navigator), so it never
 * suppresses bundling/SSR.
 *
 * @param {string} [userAgent] - defaults to navigator.userAgent
 * @returns {boolean}
 */
const isLikelyBotOrHeadless = (userAgent) => {
  if (typeof navigator === 'undefined') return false;
  const ua = userAgent ?? navigator.userAgent ?? '';

  if (ua === 'cca-app-tester') return true;
  if (isBot(ua)) return true;
  if (navigator.webdriver === true) return true;
  if (
    isChromeLikeUA(ua) &&
    typeof window !== 'undefined' &&
    typeof window.chrome === 'undefined'
  ) {
    return true;
  }
  if (hasNoPluginsAndNoLanguages()) return true;

  return false;
};

module.exports = {
  isBot,
  isChromeLikeUA,
  hasNoPluginsAndNoLanguages,
  isLikelyBotOrHeadless,
};
